/**
 * Recharge modal — preset amounts + custom + Razorpay flow.
 *
 * Mirrors the RechargeSheet from secretary-pwa/Settings.jsx:
 *   1. User picks ₹49 / ₹500 / ₹1000 / ₹2000 or enters custom amount (min ₹49)
 *   2. POST /api/v1/billing/topup → order_id, key_id, amount, currency
 *   3. Open Razorpay checkout (loaded from CDN, dynamic)
 *   4. On handler callback, POST /api/v1/billing/verify_payment with signature
 *   5. On success, fire `axondesktop.billing.refreshed` event so BalancePill
 *      and any other watchers re-fetch immediately
 */

import { useState } from 'react';
import {
  X,
  CreditCard,
  Loader2,
  CheckCircle,
  AlertCircle,
  IndianRupee,
  ShieldCheck,
} from 'lucide-react';
import clsx from 'clsx';
import {
  billingApi,
  loadRazorpay,
  readBalance,
  currencySymbol,
  type BalanceData,
} from '@/lib/billing';
import { getCachedUser } from '@/lib/auth';

interface RechargeProps {
  open: boolean;
  onClose: () => void;
  /** Latest balance — used to show current credits inside the modal. */
  balance: BalanceData | null | undefined;
}

const PRESET_AMOUNTS = [49, 500, 1000, 2000];
const MIN_AMOUNT = 49;
const MAX_AMOUNT = 100_000;

type Step = 'pick' | 'paying' | 'done' | 'error';

export function Recharge({ open, onClose, balance }: RechargeProps) {
  const [selected, setSelected] = useState<number | null>(null);
  const [custom, setCustom] = useState<string>('');
  const [step, setStep] = useState<Step>('pick');
  const [errMsg, setErrMsg] = useState<string>('');
  const [creditsAdded, setCreditsAdded] = useState<number | null>(null);

  const amountInr = selected ?? (custom ? parseInt(custom, 10) : null);
  const valid = amountInr != null && amountInr >= MIN_AMOUNT && amountInr <= MAX_AMOUNT;

  const credits = readBalance(balance);
  const symbol = currencySymbol(balance);
  const user = getCachedUser();

  const reset = () => {
    setSelected(null);
    setCustom('');
    setStep('pick');
    setErrMsg('');
    setCreditsAdded(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handlePay = async () => {
    if (!amountInr || !valid) return;
    setStep('paying');
    setErrMsg('');

    try {
      // 1) Load Razorpay SDK
      const loaded = await loadRazorpay();
      if (!loaded) {
        throw new Error('Payment gateway unavailable. Check your connection.');
      }

      // 2) Create order on server
      const order = await billingApi.initiateTopup(amountInr);
      if (!order?.order_id) {
        throw new Error('Could not create payment order.');
      }

      // 3) Open Razorpay checkout — wrap in Promise so we can await
      await new Promise<void>((resolve, reject) => {
        if (!window.Razorpay) {
          reject(new Error('Razorpay SDK not initialised'));
          return;
        }
        const rzp = new window.Razorpay({
          key: order.key_id,
          amount: order.amount,
          currency: order.currency || 'INR',
          order_id: order.order_id,
          name: 'AxonBOS',
          description: `Add ₹${amountInr} credits`,
          theme: { color: '#0f172a' },
          prefill: {
            email: user?.email,
            name: user?.full_name,
          },
          modal: {
            ondismiss: () => reject(new Error('cancelled')),
          },
          handler: async (response) => {
            try {
              const verified = await billingApi.verifyPayment(
                response.razorpay_payment_id,
                response.razorpay_order_id,
                response.razorpay_signature,
              );
              setCreditsAdded(verified.credits_added ?? amountInr);
              resolve();
            } catch (e) {
              reject(e);
            }
          },
        });
        rzp.open();
      });

      // 4) Tell BalancePill (and any other watcher) to refresh
      window.dispatchEvent(new CustomEvent('axondesktop.billing.refreshed'));
      setStep('done');
    } catch (e) {
      if ((e as Error).message === 'cancelled') {
        setStep('pick');
      } else {
        setErrMsg((e as Error).message || 'Payment failed. Please try again.');
        setStep('error');
      }
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={handleClose}
    >
      <div
        className="bg-card border border-border rounded-lg shadow-2xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <IndianRupee className="w-4 h-4 text-primary" />
            <h2 className="font-semibold">Add Credits</h2>
          </div>
          <button
            onClick={handleClose}
            className="p-1 rounded hover:bg-muted"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5">
          {/* Current balance */}
          <div className="flex items-center gap-3 px-3 py-3 rounded-lg bg-primary/8 border border-primary/20 mb-5">
            <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center">
              <IndianRupee className="w-5 h-5 text-primary" />
            </div>
            <div>
              <div className="text-[11px] text-muted-foreground">
                Available Balance
              </div>
              <div className="text-xl font-bold">
                {symbol}
                {credits.toFixed(2)}
              </div>
            </div>
          </div>

          {step === 'done' ? (
            <SuccessView
              amount={creditsAdded ?? amountInr ?? 0}
              onClose={handleClose}
            />
          ) : step === 'error' ? (
            <ErrorView msg={errMsg} onRetry={() => setStep('pick')} />
          ) : (
            <PickView
              selected={selected}
              custom={custom}
              amountInr={amountInr}
              valid={valid}
              busy={step === 'paying'}
              onSelect={(amt) => {
                setSelected(amt);
                setCustom('');
              }}
              onCustom={(v) => {
                setCustom(v);
                setSelected(null);
              }}
              onPay={handlePay}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function PickView({
  selected,
  custom,
  amountInr,
  valid,
  busy,
  onSelect,
  onCustom,
  onPay,
}: {
  selected: number | null;
  custom: string;
  amountInr: number | null;
  valid: boolean;
  busy: boolean;
  onSelect: (n: number) => void;
  onCustom: (v: string) => void;
  onPay: () => void;
}) {
  const tooSmall = custom && parseInt(custom, 10) < MIN_AMOUNT;

  return (
    <div className="space-y-5">
      {/* Preset amounts */}
      <div>
        <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-2">
          Select Amount
        </p>
        <div className="grid grid-cols-4 gap-2">
          {PRESET_AMOUNTS.map((amt) => (
            <button
              key={amt}
              type="button"
              onClick={() => onSelect(amt)}
              className={clsx(
                'py-2.5 rounded-lg border text-sm font-semibold transition-colors',
                selected === amt
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border hover:border-primary/50',
              )}
            >
              ₹{amt.toLocaleString('en-IN')}
            </button>
          ))}
        </div>
      </div>

      {/* Custom amount */}
      <div>
        <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-2">
          Custom Amount
        </p>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
            ₹
          </span>
          <input
            type="number"
            min={MIN_AMOUNT}
            max={MAX_AMOUNT}
            placeholder="Enter amount"
            value={custom}
            onChange={(e) => onCustom(e.target.value)}
            className="w-full pl-7 pr-3 py-2.5 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        {tooSmall && (
          <p className="text-[11px] text-amber-600 mt-1">
            Minimum recharge is ₹{MIN_AMOUNT}
          </p>
        )}
      </div>

      <button
        type="button"
        onClick={onPay}
        disabled={busy || !valid}
        className="w-full py-3 rounded-lg bg-primary text-primary-foreground text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-40 transition-opacity"
      >
        {busy ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" /> Processing…
          </>
        ) : (
          <>
            <CreditCard className="w-4 h-4" />
            Pay {amountInr ? `₹${amountInr.toLocaleString('en-IN')}` : ''}
          </>
        )}
      </button>

      <div className="text-[10px] text-muted-foreground text-center flex items-center justify-center gap-1.5">
        <ShieldCheck className="w-3 h-3" />
        Secured by Razorpay · Credits never expire
      </div>
    </div>
  );
}

function SuccessView({ amount, onClose }: { amount: number; onClose: () => void }) {
  return (
    <div className="flex flex-col items-center py-6 gap-3">
      <div className="w-14 h-14 rounded-full bg-green-500/15 flex items-center justify-center">
        <CheckCircle className="w-7 h-7 text-green-600" />
      </div>
      <p className="text-base font-semibold">Payment successful!</p>
      <p className="text-sm text-muted-foreground text-center">
        ₹{amount.toLocaleString('en-IN')} has been added to your balance.
      </p>
      <button
        onClick={onClose}
        className="mt-3 px-6 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium"
      >
        Done
      </button>
    </div>
  );
}

function ErrorView({ msg, onRetry }: { msg: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center py-6 gap-3">
      <div className="w-14 h-14 rounded-full bg-destructive/15 flex items-center justify-center">
        <AlertCircle className="w-7 h-7 text-destructive" />
      </div>
      <p className="text-base font-semibold">Payment failed</p>
      <p className="text-sm text-destructive text-center max-w-xs">{msg}</p>
      <button
        onClick={onRetry}
        className="mt-3 px-6 py-2 rounded-lg border border-border text-sm font-medium hover:bg-muted"
      >
        Try again
      </button>
    </div>
  );
}
